import React, { FC, useState } from 'react';
import { Grid, Typography } from '@material-ui/core';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import {
  AreaChart as ReChartsAreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReChartsTooltip,
  ResponsiveContainer,
  Legend,
  LegendPayload,
  LegendProps,
} from 'recharts';

import { IndividualStat, ReachDailyStat } from 'api';
import { formatStat } from 'helpers';

import { getColor } from '../helpers';
import { Tooltip } from '../shared';

export interface IAreaChartConfig {
  name: string;
  dataKey: string;
  value: IndividualStat;
}

interface IProps {
  height: number;
  data: ReachDailyStat[];
  config: IAreaChartConfig[];
}

const useStyles = makeStyles({
  dot: {
    width: 18,
    height: 18,
    borderRadius: '50%',
  },
});

const AreaChart: FC<IProps> = ({ height, data, config }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [legendValues] = useState(getLegendValues());

  const gridColor = theme.palette.grey[300];
  const margin = { top: 24, right: 0, bottom: 0, left: 0 };
  const wrapperStyle = { bottom: 0 };

  // ReCharts will keep re-rendering if we don't memoize the legend values
  function getLegendValues() {
    const values: number[] = [];
    config.forEach(({ value }) => {
      values.push(value.count);
    });
    return values;
  }

  function renderLegendItem(payload: LegendPayload, index: number) {
    const legendBgColor = getColor(index);

    return (
      <Grid item key={index}>
        <Grid container alignItems="center" justify="space-between">
          <Grid item>
            <Grid container alignItems="center" spacing={1}>
              <Grid item>
                <div className={classes.dot} style={{ background: legendBgColor }} />
              </Grid>
              <Grid item>
                <Typography display="inline">{payload.value}</Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Typography>{formatStat(legendValues[index])}</Typography>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  function renderLegend({ payload }: LegendProps) {
    return (
      <Grid container direction="column" spacing={1}>
        {payload.map(renderLegendItem)}
      </Grid>
    );
  }

  return (
    <ResponsiveContainer height={height}>
      <ReChartsAreaChart data={data} margin={margin}>
        <CartesianGrid stroke={gridColor} vertical={false} />
        <XAxis
          dataKey="calendar_day"
          tickLine={false}
          axisLine={{ stroke: { gridColor } }}
          tickMargin={10}
          tickFormatter={() => ''}
        />
        <YAxis
          tickLine={false}
          orientation="right"
          axisLine={{ stroke: { gridColor } }}
          tickMargin={10}
        />
        <Legend wrapperStyle={wrapperStyle} content={renderLegend} />
        <ReChartsTooltip content={<Tooltip />} />
        {config.map((entry, index) => (
          <Area
            key={entry.dataKey}
            type="monotone"
            dataKey={entry.dataKey}
            name={entry.name}
            stackId="1"
            stroke={getColor(index)}
            fill={getColor(index)}
          />
        ))}
      </ReChartsAreaChart>
    </ResponsiveContainer>
  );
};

export default AreaChart;
